import { HOURS_MAX, MAX_HOURS_TIME_INPUT_12H, MINUTES_MAX } from "@/src/shared/constants/timeEntry";
import {
  DIGIT_ZEROS,
  FULL_WIDTH_COLON_REGEX,
  NON_ASCII_DIGIT_REGEX,
  TIME_OF_DAY_REGEX,
} from "@/src/shared/constants/timeOfDay";
import timeEntryUtils from "@/src/shared/utils/timeEntry";

const toAsciiDigit = (digit: string): string => {
  const codePoint = digit.codePointAt(0)!;
  const zero = DIGIT_ZEROS.find(start => codePoint >= start && codePoint < start + 10)!;
  return String(codePoint - zero);
};

/**
 * Reads a typed time of day such as 9, 930, 1045, 10:45, 10.45, 10h45, 10h, 2:30pm, 02:30 PM or 9a.
 * Digits without am/pm are 24-hour. A comma is not a separator because duration fields read it as a decimal sign.
 * @param text typed input
 * @returns "HH:mm", or null when the text is not a time between 00:00 and 23:59
 */
export const parseTimeOfDay = (text: string): string | null => {
  const normalized = text
    .replace(NON_ASCII_DIGIT_REGEX, toAsciiDigit)
    .replace(FULL_WIDTH_COLON_REGEX, ":")
    .replace(/\s+/g, "")
    .toLowerCase();
  const match = normalized.match(TIME_OF_DAY_REGEX);
  if (!match) return null;

  let hours = Number(match[1]);
  const minutes = Number(match[2] ?? 0);
  const meridiem = match[3];
  if (meridiem) {
    if (hours < 1 || hours > MAX_HOURS_TIME_INPUT_12H) return null;
    hours = (hours % MAX_HOURS_TIME_INPUT_12H) + (meridiem === "p" ? MAX_HOURS_TIME_INPUT_12H : 0);
  }
  if (hours >= HOURS_MAX || minutes >= MINUTES_MAX) return null;

  return `${timeEntryUtils.padTimePart(hours)}:${timeEntryUtils.padTimePart(minutes)}`;
};
